import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { commonService } from '../Network/services/commonService';
import { isMobileDevice } from '../utils/deviceDetection';
import './LinkedInAuth.css';

const CONFIG = {
  APP_DEEP_LINK: 'gigfws://linkedin-auth',
  MOBILE_REDIRECT_DELAY: 2000,
  WEB_REDIRECT_DELAY: 1000
};

const LinkedInAuth = () => {
  const [message, setMessage] = useState('Authenticating your LinkedIn account... this will just take a moment.');
  const [countdown, setCountdown] = useState(3);
  const [apiCompleted, setApiCompleted] = useState(false);
  const [showFewSeconds, setShowFewSeconds] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [deepLinkAttempted, setDeepLinkAttempted] = useState(false);
  const location = useLocation();
  const history = useHistory();
  const redirectTimeoutRef = useRef(null);
  const deepLinkTimeoutRef = useRef(null);

  const cleanup = () => {
    if (redirectTimeoutRef.current) {
      clearTimeout(redirectTimeoutRef.current);
    }
    if (deepLinkTimeoutRef.current) {
      clearTimeout(deepLinkTimeoutRef.current);
    }
  };

  const redirectToMobileApp = (code, state, error) => {
    if (deepLinkAttempted) return;
    setDeepLinkAttempted(true);
    
    let redirectUrl = CONFIG.APP_DEEP_LINK;
    const params = new URLSearchParams();
    
    if (code) {
      params.append('code', code);
    }
    if (state) {
      params.append('state', state);
    }
    if (error) {
      params.append('error', error);
    }
    
    if (params.toString()) {
      redirectUrl += '?' + params.toString();
    }
    
    window.location.href = redirectUrl;
    
    deepLinkTimeoutRef.current = setTimeout(() => {
    }, CONFIG.MOBILE_REDIRECT_DELAY);
  };

  const safeRedirect = (delay = 1000) => {
    if (isRedirecting) return;
    setIsRedirecting(true);
    
    redirectTimeoutRef.current = setTimeout(() => {
      try {
        history.push("/");
      } catch (error) {
        window.location.href = '/';
      }
    }, delay);
  };

  const processLinkedInAuth = async (code, redirectUrlType) => {
    try {
      const data = {
        code: code,
        redirect_url: redirectUrlType
      }
      const formData = new URLSearchParams();
      for (const key in data) {
        formData.append(key, data[key]);
      }
      
      const response = await commonService.CommonService(
        "linkedin/loginHandler",
        formData
      );
      
      if (isRedirecting) return;
      
      setApiCompleted(true);
      
      if (response.status == 200) {
        if (response.data && response.data[0]) {
          window.localStorage.setItem("UserData", JSON.stringify(response.data[0]));
          window.localStorage.setItem("email", response.data[0].email_id);
          window.localStorage.setItem("mobile", response.data[0].mobile_number);
          window.localStorage.setItem("auth_token", response.data[0].auth_token);
          window.localStorage.setItem("isLoggedIn", true);
          window.localStorage.setItem("categories_with_sub_categories", JSON.stringify(response.data[0].categories_with_sub_categories));
          
          window.dispatchEvent(new CustomEvent('linkedinAuthComplete'));
          
          setMessage("Authentication successful! Redirecting...");
          
          if (isMobile && deepLinkAttempted) {
            safeRedirect(3000);
          } else {
            safeRedirect(1000);
          }
        } else {
          toast.error("Login failed: No user data received", {
            position: "top-right",
            autoClose: 5000,
          });
        }
      } else {
        toast.error(response.message);
        setMessage("Authentication failed. Redirecting...");
        safeRedirect(1000);
      }
    } catch (err) {
      if (!isRedirecting) {
        toast.error("LinkedIn authentication failed.");
        setApiCompleted(true);
        setMessage("Authentication failed. Redirecting...");
        safeRedirect(1000);
      }
    }
  };

  useEffect(() => {
    const mobile = isMobileDevice();
    setIsMobile(mobile);
    
    const params = new URLSearchParams(location.search);
    const code = params.get("code");
    const error = params.get("error");
    const state = params.get("state");

    if (error) {
      toast.error("LinkedIn login failed.");
      if (mobile) {
        redirectToMobileApp(null, state, error);
        setTimeout(() => {
          safeRedirect(3000);
        }, CONFIG.MOBILE_REDIRECT_DELAY);
      } else {
        safeRedirect(3000);
      }
      return;
    }

    if (!code) {
      toast.error("No authorization code received.");
      if (mobile) {
        redirectToMobileApp(null, state, 'no_code');
        setTimeout(() => {
          safeRedirect(3000);
        }, CONFIG.MOBILE_REDIRECT_DELAY);
      } else {
        safeRedirect(3000);
      }
      return;
    }

    if (mobile && !deepLinkAttempted) {
      redirectToMobileApp(code, state);
      
      deepLinkTimeoutRef.current = setTimeout(() => {
        processLinkedInAuth(code, 'linkedin-mobile-auth');
      }, CONFIG.MOBILE_REDIRECT_DELAY);
    } else {
      processLinkedInAuth(code, 'linkedin-web-auth');
    }

    return cleanup;
  }, [location.search]);

  useEffect(() => {
    if (!apiCompleted && !isRedirecting) {
      const countdownInterval = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(countdownInterval);
            setShowFewSeconds(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(countdownInterval);
    }
  }, [apiCompleted, isRedirecting]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && isMobile && deepLinkAttempted && apiCompleted) {
        cleanup();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isMobile, deepLinkAttempted, apiCompleted]);

  return (
    <div className="linkedin-auth-container">
      <div className="auth-card">
        <div className="logo-container">
          <div className="linkedin-logo">
            <img src="/assets/images/linkedin.png" alt="LinkedIn Logo" />
          </div>
          <div className="arrow-animation">
            <span className="arrow-text">&gt;&gt;&gt;</span>
          </div>
          <div className="fws-logo">
            <img src="/assets/images/newlogo.png" alt="FWS Logo" />
          </div>
        </div>
        
        <h2 className="auth-title">
          LinkedIn Authentication
        </h2>
        
        <p className="auth-subtitle">
          {message}
        </p>
        
        <p className="countdown-text">
          {showFewSeconds ? 'Redirecting in few seconds...' : `Redirecting in ${countdown} Seconds...`}
        </p>
        
        <div className="info-section">
          <h4>What's Happening?</h4>
          <ul>
            <li>Verifying your LinkedIn Credentials</li>
            <li>Extracting your account information</li>
            <li>Setting your profile</li>
            <li>Redirecting you to the main application</li>
          </ul>
        <div className="divider"></div>
        
        <div className="security-section">
          <div className="security-icon">
            <img src="/assets/images/SecureProcessIcon.svg" alt="Secure Process" />
          </div>
          <p className="security-text">
            <strong>Secure Process:</strong> Your LinkedIn data is processed securely and only used to create or login to your account
          </p>
        </div>
        </div>
        
      </div>
    </div>
  );
};

export default LinkedInAuth;
